import React, { useEffect, useState } from 'react';

import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import { useAppDispatch } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { getCaseIntake } from '@brands/services/cases/multiStage';
import { clearLinkedCase } from '@brands/store/slices/linkedCaseSlice';

// Handles a wizard that was already open when linked CC became exempt from
// checkout. Confirm follow-up identity before swapping checkout for the
// Jotform popup and clearing its context.
const LinkedCareCoordinationRedirect = ({
  caseId,
  children,
}: {
  caseId: number | null;
  children: React.ReactNode;
}): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const roleNavigate = useRoleNavigate();
  const [result, setResult] = useState<{ caseId: number; exempt: boolean; failed?: boolean }>();
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (caseId == null) return;
    let active = true;
    getCaseIntake(caseId).then(
      (intake) => {
        if (active) setResult({ caseId, exempt: intake.is_linked_care_coordination === true });
      },
      () => {
        if (active) setResult({ caseId, exempt: false, failed: true });
      },
    );
    return () => {
      active = false;
    };
  }, [caseId, attempt]);
  useEffect(() => {
    if (caseId == null || result?.caseId !== caseId || !result.exempt) return;
    roleNavigate(`/care-coordination-form/${caseId}`, { replace: true });
    dispatch(clearLinkedCase());
  }, [caseId, result, dispatch, roleNavigate]);
  if (caseId == null) return <>{children}</>;
  if (result?.caseId !== caseId) return <Loading fullScreen />;
  if (result.failed) {
    return (
      <div role="alert">
        Unable to check this case’s requirements.{' '}
        <button
          type="button"
          onClick={() => {
            setResult(undefined);
            setAttempt((value) => value + 1);
          }}
        >
          Try again
        </button>
      </div>
    );
  }
  return result.exempt ? null : <>{children}</>;
};

export default LinkedCareCoordinationRedirect;
